import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const CourtBookingContainer = styled.div`
  padding: 0;
`;

const SearchSection = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
`;

const CantonAccordion = styled.div`
  margin-bottom: 1.5rem;
`;

const CantonRow = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const CantonHeader = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${props => props.isOpen ? 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)' : 'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.isOpen ? 'white' : '#1f2937'};
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.isOpen ? 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)' : 'rgba(4, 54, 99, 0.1)'};
    color: ${props => props.isOpen ? 'white' : '#043663'};
  }
`;

const CantonName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CantonCount = styled.span`
  background: ${props => props.isOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(4, 54, 99, 0.1)'};
  color: ${props => props.isOpen ? 'white' : '#043663'};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
`;

const ExpandIcon = styled.div`
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const CantonContent = styled.div`
  max-height: ${props => props.isOpen ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: rgba(255, 255, 255, 0.95);
`;

const ShowAllButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #d52b1e 0%, #f56565 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  margin-bottom: 1rem;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(213, 43, 30, 0.3);
  }
`;

const CantonCourtsList = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  margin-top: 1rem;
`;

const CourtListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(4, 54, 99, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CourtInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CourtName = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CourtDetails = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CourtBookButton = styled.a`
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(4, 54, 99, 0.3);
    color: white;
  }
`;

const CourtInfoButton = styled.div`
  padding: 0.5rem 1rem;
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: default;
`;

// Keep the old components for fallback if needed
// eslint-disable-next-line no-unused-vars
const CourtsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const NoCourtsMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`;

// eslint-disable-next-line no-unused-vars
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem;
`;

// eslint-disable-next-line no-unused-vars
const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #043663;
    color: #043663;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// eslint-disable-next-line no-unused-vars
const PaginationInfo = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 600;
`;

const CourtBooking = () => {
  const { t } = useTranslation();
  const [courts, setCourts] = useState([]);
  const [filteredCourts, setFilteredCourts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [openCantons, setOpenCantons] = useState({});
  const [viewMode, setViewMode] = useState('canton'); // 'canton' or 'all'

  const loadCourts = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      // Same axios client as the rest of the app (correct API_BASE_URL in web + Electron desktop)
      const data = await api.get('/courts', { params: { limit: 10000 } });
      if (data.success && Array.isArray(data.courts)) {
        setCourts(data.courts);
        setFilteredCourts(data.courts);
      } else {
        setCourts([]);
        setFilteredCourts([]);
        setLoadError(data?.message || t('courtBooking.loadErrorFallback'));
      }
    } catch (error) {
      console.error('Error loading courts:', error);
      setCourts([]);
      setFilteredCourts([]);
      setLoadError(
        error?.response?.data?.message || error?.message || t('courtBooking.loadErrorNetwork'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCourts();
  }, [loadCourts]);

  useEffect(() => {
    const performSearch = () => {
      let filtered = courts;
      
      // Filter by search query
      if (searchQuery.trim() !== '') {
        const searchTerm = searchQuery.toLowerCase().trim();
        filtered = filtered.filter(court => {
          const name = court.name ? court.name.toLowerCase() : '';
          const location = court.location ? court.location.toLowerCase() : '';
          const description = court.description ? court.description.toLowerCase() : '';
          const address = court.address ? court.address.toLowerCase() : '';
          
          return name.includes(searchTerm) ||
                 location.includes(searchTerm) ||
                 description.includes(searchTerm) ||
                 address.includes(searchTerm);
        });
      }
      
      setFilteredCourts(filtered);
    };

    performSearch();
  }, [searchQuery, courts]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleCantonToggle = (canton) => {
    setOpenCantons(prev => ({
      ...prev,
      [canton]: !prev[canton]
    }));
  };

  const handleShowAll = () => {
    setViewMode(viewMode === 'all' ? 'canton' : 'all');
    setSearchQuery(''); // Clear search when switching modes
  };

  // Get unique cantons from courts with full names
  const getCantonsWithData = () => {
    const cantonMap = {
      'GE': 'Geneva',
      'ZH': 'Zurich', 
      'VD': 'Vaud',
      'BE': 'Bern',
      'AG': 'Aargau',
      'SG': 'St. Gallen',
      'BS': 'Basel-Stadt',
      'BL': 'Basel-Landschaft',
      'Basel': 'Basel',
      'TI': 'Ticino',
      'LU': 'Lucerne',
      'TG': 'Thurgau',
      'GR': 'Grisons',
      'GL': 'Glarus',
      'SO': 'Solothurn',
      'FR': 'Fribourg',
      'SZ': 'Schwyz',
      'SH': 'Schaffhausen',
      'AR': 'Appenzell Ausserrhoden',
      'AI': 'Appenzell Innerrhoden',
      'Appenzell': 'Appenzell',
      'NW': 'Nidwalden',
      'Nidwalden': 'Nidwalden',
      'OW': 'Obwalden',
      'UR': 'Uri',
      'JU': 'Jura',
      'NE': 'Neuchâtel',
      'VS': 'Valais',
      'ZG': 'Zug'
    };

    const cantons = [...new Set(courts.map(court => court.location).filter(Boolean))];
    return cantons.map(canton => ({
      code: canton,
      name: cantonMap[canton] || canton,
      courts: courts.filter(court => court.location === canton).sort((a, b) => a.name.localeCompare(b.name))
    })).sort((a, b) => a.name.localeCompare(b.name));
  };

  if (loading) {
    return (
      <CourtBookingContainer>
        <LoadingMessage>{t('courtBooking.loadingCourts')}</LoadingMessage>
      </CourtBookingContainer>
    );
  }

  if (loadError) {
    return (
      <CourtBookingContainer>
        <SearchSection>
          <div style={{ color: '#b91c1c', fontWeight: 600, marginBottom: '0.5rem' }}>{t('courtBooking.errorTitle')}</div>
          <div style={{ color: '#475569', fontSize: '0.9375rem', lineHeight: 1.5 }}>{loadError}</div>
          <ShowAllButton type="button" style={{ marginTop: '1rem' }} onClick={() => loadCourts()}>
            {t('courtBooking.tryAgain')}
          </ShowAllButton>
        </SearchSection>
      </CourtBookingContainer>
    );
  }

  return (
    <CourtBookingContainer>
      <SearchSection>
        <SearchInput
          type="text"
          placeholder={t('courtBooking.searchPlaceholder')}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        
        {searchQuery.trim() === '' && (
          <ShowAllButton onClick={handleShowAll}>
            {viewMode === 'all' ? t('courtBooking.showByCanton') : t('courtBooking.showAllCourts')} ({courts.length})
          </ShowAllButton>
        )}

        {viewMode === 'canton' && searchQuery.trim() === '' && (
          <CantonAccordion>
            <div style={{ marginBottom: '1rem', fontWeight: '600', color: '#1f2937' }}>
              {t('courtBooking.browseByCanton')}
            </div>
            {getCantonsWithData().map((canton) => (
              <CantonRow key={canton.code}>
                <CantonHeader
                  isOpen={openCantons[canton.code]}
                  onClick={() => handleCantonToggle(canton.code)}
                >
                  <CantonName>
                    <span>🏛️</span>
                    <span>{canton.name} ({canton.code})</span>
                    <CantonCount isOpen={openCantons[canton.code]}>
                      {t('courtBooking.courtsCountBadge', { count: canton.courts.length })}
                    </CantonCount>
                  </CantonName>
                  <ExpandIcon isOpen={openCantons[canton.code]}>
                    ▼
                  </ExpandIcon>
                </CantonHeader>
                <CantonContent isOpen={openCantons[canton.code]}>
                  <CantonCourtsList>
                    {canton.courts.map((court) => (
                      <CourtListItem key={court.id}>
                        <CourtInfo>
                          <CourtName>{court.name}</CourtName>
                          <CourtDetails>
                            {court.type && <span>🏢 {court.type}</span>}
                            {court.courtsCount && (
                              <span>🏟️ {t('courtBooking.courtVenueCourtsCount', { count: court.courtsCount })}</span>
                            )}
                            {court.indoorOutdoor && <span>🏠 {court.indoorOutdoor}</span>}
                          </CourtDetails>
                        </CourtInfo>
                        {court.name && court.name.toLowerCase().includes('jonction') ? (
                          <CourtBookButton 
                            href="https://www.padel-academy.ch/oujouer" 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            {t('courtBooking.book')}
                          </CourtBookButton>
                        ) : court.reservationLink || court.website ? (
                          <CourtBookButton 
                            href={court.reservationLink || court.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            {t('courtBooking.book')}
                          </CourtBookButton>
                        ) : (
                          <CourtInfoButton>
                            {t('courtBooking.contact')}
                          </CourtInfoButton>
                        )}
                      </CourtListItem>
                    ))}
                  </CantonCourtsList>
                </CantonContent>
              </CantonRow>
            ))}
          </CantonAccordion>
        )}
      </SearchSection>

      {/* Show search results */}
      {searchQuery.trim() !== '' && (
        <>
          {filteredCourts.length === 0 ? (
            <NoCourtsMessage>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
              <h3>{t('courtBooking.noCourtsTitle')}</h3>
              <p>{t('courtBooking.noCourtsForQuery', { query: searchQuery })}</p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '1rem' }}>
                {t('courtBooking.noCourtsHint')}
              </p>
            </NoCourtsMessage>
          ) : (
            <CantonCourtsList>
              <div style={{ 
                marginBottom: '1rem', 
                fontWeight: '600', 
                color: '#1f2937',
                fontSize: '1.1rem'
              }}>
                {t('courtBooking.searchResultsCourtsHeading', { query: searchQuery, count: filteredCourts.length })}
              </div>
              
              {filteredCourts.sort((a, b) => a.name.localeCompare(b.name)).map((court) => (
                <CourtListItem key={court.id}>
                  <CourtInfo>
                    <CourtName>{court.name}</CourtName>
                    <CourtDetails>
                      <span>📍 {court.location}</span>
                      {court.type && <span>🏢 {court.type}</span>}
                      {court.courtsCount && (
                        <span>🏟️ {t('courtBooking.courtVenueCourtsCount', { count: court.courtsCount })}</span>
                      )}
                      {court.indoorOutdoor && <span>🏠 {court.indoorOutdoor}</span>}
                    </CourtDetails>
                  </CourtInfo>
                  {court.name && court.name.toLowerCase().includes('jonction') ? (
                    <CourtBookButton 
                      href="https://www.padel-academy.ch/oujouer" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {t('courtBooking.book')}
                    </CourtBookButton>
                  ) : court.reservationLink || court.website ? (
                    <CourtBookButton 
                      href={court.reservationLink || court.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {t('courtBooking.book')}
                    </CourtBookButton>
                  ) : (
                    <CourtInfoButton>
                      {t('courtBooking.contact')}
                    </CourtInfoButton>
                  )}
                </CourtListItem>
              ))}
            </CantonCourtsList>
          )}
        </>
      )}

      {/* Show all courts when in "all" mode and not searching */}
      {viewMode === 'all' && searchQuery.trim() === '' && (
        <CantonCourtsList>
          <div style={{ 
            marginBottom: '1rem', 
            fontWeight: '600', 
            color: '#1f2937',
            fontSize: '1.1rem'
          }}>
            {t('courtBooking.allCourtsHeading', { count: courts.length })}
          </div>
          
          {courts.sort((a, b) => a.name.localeCompare(b.name)).map((court) => (
            <CourtListItem key={court.id}>
              <CourtInfo>
                <CourtName>{court.name}</CourtName>
                <CourtDetails>
                  <span>📍 {court.location}</span>
                  {court.type && <span>🏢 {court.type}</span>}
                  {court.courtsCount && (
                    <span>🏟️ {t('courtBooking.courtVenueCourtsCount', { count: court.courtsCount })}</span>
                  )}
                  {court.indoorOutdoor && <span>🏠 {court.indoorOutdoor}</span>}
                </CourtDetails>
              </CourtInfo>
              {court.name && court.name.toLowerCase().includes('jonction') ? (
                <CourtBookButton 
                  href="https://www.padel-academy.ch/oujouer" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {t('courtBooking.book')}
                </CourtBookButton>
              ) : court.reservationLink || court.website ? (
                <CourtBookButton 
                  href={court.reservationLink || court.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {t('courtBooking.book')}
                </CourtBookButton>
              ) : (
                <CourtInfoButton>
                  {t('courtBooking.contact')}
                </CourtInfoButton>
              )}
            </CourtListItem>
          ))}
        </CantonCourtsList>
      )}
    </CourtBookingContainer>
  );
};

export default CourtBooking;
